import { CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router/dom';
import { queryClient } from '@/api/queryClient';
import { AuthBootstrap } from '@/auth/AuthBootstrap';
import { toLanguage } from '@/i18n';
import { Notifier } from '@/shared/components/notifications';
import { ColorModeContext, type ColorMode } from './colorMode';
import { router } from './router';
import { buildTheme } from './theme';

const MODE_KEY = 'aml.colorMode';

function readMode(prefersDark: boolean): ColorMode {
  try {
    const stored = localStorage.getItem(MODE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* ignore */
  }
  return prefersDark ? 'dark' : 'light';
}

export function App() {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const [mode, setMode] = useState<ColorMode>(() => readMode(prefersDark));
  const { i18n } = useTranslation();
  const language = toLanguage(i18n.language);
  const theme = useMemo(() => buildTheme(mode, language), [mode, language]);
  const colorMode = useMemo(
    () => ({
      mode,
      toggle: () =>
        setMode((m) => {
          const next = m === 'light' ? 'dark' : 'light';
          try {
            localStorage.setItem(MODE_KEY, next);
          } catch {
            /* ignore */
          }
          return next;
        }),
    }),
    [mode],
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <QueryClientProvider client={queryClient}>
          <AuthBootstrap>
            <RouterProvider router={router} />
          </AuthBootstrap>
          <Notifier />
        </QueryClientProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
